import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useProperties } from "@/hooks/useProperties";
import { useUsers } from "@/hooks/useUsers";
import { Check, Clock, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  EnrichedTask,
  useUpdateTask,
  useDeleteTask,
  useTaskTags,
} from "@/hooks/admin/AdminTodosTabs/useTasks";
import CustomDatePicker from "@/components/CustomDatePicker";
import { format, parse } from "date-fns";
import { translateRoleToSpanish } from "@/utils/shared/translateRoleToSpanish";
import useRolePermissions from "@/hooks/admin/shared/useRolePermissions";

interface TaskDetailModalProps {
  task: EnrichedTask | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TaskDetailModal = ({ task, open, onOpenChange }: TaskDetailModalProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignedUserId, setAssignedUserId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [tagId, setTagId] = useState("");
  const [status, setStatus] = useState<"pending" | "completed">(
    task?.status || "pending"
  );

  const { role } = useRolePermissions();
  const isReadOnly = role === "cleaning";

  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const { data: tags = [] } = useTaskTags();
  const { data: users = [] } = useUsers(["super_admin", "cleaning"]);
  const { data: properties = [] } = useProperties();

  const availableUsers = users.filter((user) =>
    ["super_admin", "cleaning"].includes(user.role)
  );

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description || "");
      setDueDate(task.due_date || "");
      setAssignedUserId(task.assigned_user_id || "__none__");
      setPropertyId(task.property_id || "__none__");
      setTagId(task.tag_id || "__none__");
      setStatus(task.status);
    }
  }, [task]);

  if (!task) return null;

  const handleSave = () => {
    const updates = {
      title: title.trim(),
      description: description.trim() || null,
      due_date: dueDate || null,
      assigned_user_id:
        assignedUserId === "__none__" ? null : assignedUserId || null,
      property_id: propertyId === "__none__" ? null : propertyId || null,
      tag_id: tagId === "__none__" ? null : tagId || null,
    };

    updateTask.mutate(
      {
        id: task.id,
        updates,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  const handleToggleComplete = () => {
    const newStatus = status === "completed" ? "pending" : "completed";
    const updates: any = {
      status: newStatus,
      completed_at: newStatus === "completed" ? new Date().toISOString() : null,
    };

    updateTask.mutate({
      id: task.id,
      updates,
    });

    setStatus(newStatus);
  };

  const handleDelete = () => {
    if (confirm("¿Estás seguro de que quieres eliminar esta tarea?")) {
      deleteTask.mutate(task.id, {
        onSuccess: () => {
          onOpenChange(false);
        },
      });
    }
  };

  const isCompleted = status === "completed";
  const parsedDate = dueDate
    ? parse(dueDate, "yyyy-MM-dd", new Date())
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Editar Tarea</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="edit-title">Título *</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título de la tarea"
              disabled={isReadOnly}
            />
          </div>

          <div>
            <Label htmlFor="edit-description">Descripción</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción de la tarea"
              rows={3}
              disabled={isReadOnly}
            />
          </div>

          <div>
            <Label htmlFor="edit-dueDate">Fecha de vencimiento</Label>
            <CustomDatePicker
              selected={parsedDate}
              onSelect={(date) =>
                setDueDate(date ? format(date, "yyyy-MM-dd") : "")
              }
              placeholder="Selecciona una fecha"
              isAdminContext={true}
              className="flex-1 h-10"
              disabled={isReadOnly}
            />
          </div>

          <div>
            <Label htmlFor="edit-assignedUser">Usuario asignado</Label>
            <Select
              value={assignedUserId}
              onValueChange={setAssignedUserId}
              disabled={isReadOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar usuario" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin asignar</SelectItem>
                {availableUsers.map((user) => (
                  <SelectItem
                    key={user.user_id}
                    value={user.user_id || `user-${user.id}`}
                  >
                    {user.first_name} {user.last_name}
                    {" - "}
                    <span className="text-muted-foreground text-xs">
                      {translateRoleToSpanish(user.role)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="edit-property">Propiedad</Label>
            <Select
              value={propertyId}
              onValueChange={setPropertyId}
              disabled={isReadOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar propiedad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin propiedad asignada</SelectItem>
                <SelectItem value="__external__">Propiedad externa</SelectItem>
                {properties.map((property) => (
                  <SelectItem key={property?.id} value={property?.id}>
                    {property?.shortName || property?.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="edit-tag">Etiqueta</Label>
            <Select value={tagId} onValueChange={setTagId} disabled={isReadOnly}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar etiqueta" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin etiqueta</SelectItem>
                {tags
                  .filter((tag) => tag.id)
                  .map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {!isReadOnly && (
            <div className="flex gap-2 pt-4">
              <Button
                variant={isCompleted ? "default" : "outline"}
                onClick={handleToggleComplete}
                disabled={updateTask.isPending}
                className={isCompleted ? "bg-green-600 hover:bg-green-700" : ""}
              >
                {isCompleted ? (
                  <Check className="h-4 w-4 mr-2" />
                ) : (
                  <Clock className="h-4 w-4 mr-2" />
                )}
                {isCompleted
                  ? "Marcar como pendiente"
                  : "Marcar como completada"}
              </Button>

              <Button
                onClick={handleSave}
                disabled={updateTask.isPending || !title.trim()}
              >
                {updateTask.isPending ? "Guardando..." : "Guardar"}
              </Button>

              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>

              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={deleteTask.isPending}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailModal;
